import React, { useState, useEffect } from 'react';
import Toast from './Toast';
import ConfirmDialog from './ConfirmDialog';
import { ADMIN_API_ENDPOINTS, FRONTEND_URL, SERVER_URL } from '../config/api';

const ProductManagement = () => {
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(null); // 跟踪哪个图片正在上传
  const [uploadingHistoryImage, setUploadingHistoryImage] = useState(null); // 跟踪哪个历史简介图片正在上传
  const [showEditor, setShowEditor] = useState(false);
  const [currentProduct, setCurrentProduct] = useState(null);
  const [toast, setToast] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null);

  // 统一的token key
  const ADMIN_TOKEN_KEY = 'metalbox_admin_token';

  // 获取完整图片URL的辅助函数
  const getImageUrl = (imagePath) => {
    if (!imagePath) return '';
    // 如果已经是完整URL,直接返回
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      return imagePath;
    }
    // 如果是相对路径,添加SERVER_URL前缀
    return `${SERVER_URL}${imagePath.startsWith('/') ? '' : '/'}${imagePath}`;
  };

  // 上传图片
  const handleImageUpload = async (e, index) => {
    const file = e.target.files[0];
    if (!file) return;

    // 验证文件类型
    if (!file.type.startsWith('image/')) {
      showToast('请选择图片文件', 'error');
      return;
    }

    // 验证文件大小 (30MB)
    if (file.size > 30 * 1024 * 1024) {
      showToast('图片大小不能超过30MB', 'error');
      return;
    }

    // 如果没有产品ID,不能上传图片
    if (!currentProduct.product_id) {
      showToast('请先生成产品ID后再上传图片', 'error');
      e.target.value = '';
      return;
    }

    const formData = new FormData();
    formData.append('image', file);
    formData.append('productId', currentProduct.product_id);

    try {
      setIsLoading(true);
      setUploadingImage(index); // 设置当前上传的图片索引
      const response = await fetch(ADMIN_API_ENDPOINTS.PRODUCTS.UPLOAD_IMAGE, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem(ADMIN_TOKEN_KEY)}`,
        },
        body: formData,
      });

      const data = await response.json();

      if (data.success) {
        // 更新图片URL - 创建新数组确保React检测到变化
        const newImages = [...(currentProduct.images || [])];
        newImages[index] = data.imageUrl;

        // 使用函数式更新确保获取最新状态
        setCurrentProduct((prev) => ({
          ...prev,
          images: newImages,
          // 添加时间戳强制重新渲染
          _imageUpdateTimestamp: Date.now(),
        }));

        showToast('图片上传成功', 'success');
      } else {
        showToast(data.message || '上传失败', 'error');
      }
    } catch (error) {
      console.error('Upload error:', error);
      showToast('上传失败', 'error');
    } finally {
      setIsLoading(false);
      setUploadingImage(null); // 清除上传状态
      // 清空文件输入,允许重新上传相同文件
      e.target.value = '';
    }
  };

  // 产品模板
  const productTemplate = {
    product_id: '',
    name: { en: '', zh: '' },
    description: { en: '', zh: '' },
    specifications: {}, // 动态参数,初始为空
    history: { en: '', zh: '' },
    historyItems: [], // 历史简介组
    referenceLinks: [], // 参考链接
    images: [],
    status: 'draft', // draft, published
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };

  // 获取产品列表
  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(ADMIN_API_ENDPOINTS.PRODUCTS.LIST, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem(ADMIN_TOKEN_KEY)}`,
        },
      });

      if (response.status === 401 || response.status === 403) {
        // Token无效或过期，清除token并刷新页面
        localStorage.removeItem(ADMIN_TOKEN_KEY);
        showToast('登录已过期，请重新登录', 'error');
        setTimeout(() => {
          window.location.reload();
        }, 1500);
        return;
      }

      if (response.ok) {
        const data = await response.json();
        setProducts(data.products || []);
      } else {
        console.error('获取产品列表失败:', response.status, response.statusText);
        const errorText = await response.text();
        console.error('Error details:', errorText);
        showToast(`获取产品列表失败: ${response.status}`, 'error');
      }
    } catch (error) {
      console.error('获取产品列表失败:', error);
      showToast(`获取产品列表失败: ${error.message}`, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // 显示Toast
  const showToast = (message, type = 'success') => {
    setToast({ message, type });
  };

  // 创建新产品
  const handleCreateProduct = () => {
    setCurrentProduct({ ...productTemplate });
    setShowEditor(true);
  };

  // 编辑产品
  const handleEditProduct = (product) => {
    console.log('Editing product:', product);
    console.log('History Items:', product.historyItems);
    setCurrentProduct({ ...product });
    setShowEditor(true);
  };

  // 删除产品
  const handleDeleteProduct = (productId) => {
    setConfirmDialog({
      message: '确定要删除这个产品吗？此操作不可撤销。',
      type: 'danger',
      onConfirm: async () => {
        try {
          const response = await fetch(
            ADMIN_API_ENDPOINTS.PRODUCTS.BY_ID(productId),
            {
              method: 'DELETE',
              headers: {
                Authorization: `Bearer ${localStorage.getItem(
                  ADMIN_TOKEN_KEY
                )}`,
              },
            }
          );
          if (response.ok) {
            showToast('产品已删除', 'success');
            fetchProducts();
          }
        } catch (error) {
          showToast('删除失败', 'error');
        }
        setConfirmDialog(null);
      },
      onCancel: () => setConfirmDialog(null),
    });
  };

  // 保存产品
  const handleSaveProduct = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    console.log('Saving product:', currentProduct);
    console.log('Saving historyItems:', currentProduct.historyItems);

    try {
      const url = currentProduct.id
        ? ADMIN_API_ENDPOINTS.PRODUCTS.BY_ID(currentProduct.id)
        : ADMIN_API_ENDPOINTS.PRODUCTS.LIST;

      const method = currentProduct.id ? 'PUT' : 'POST';

      // 准备发送的数据
      const productData = { ...currentProduct };

      // 创建新产品时,不发送product_id(后端会自动生成)
      // 更新产品时,将product_id改名为id发送给后端
      if (currentProduct.id) {
        productData.id = currentProduct.product_id;
        delete productData.product_id;
      } else {
        // 新产品,删除product_id字段
        delete productData.product_id;
      }

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem(ADMIN_TOKEN_KEY)}`,
        },
        body: JSON.stringify(productData),
      });

      if (response.ok) {
        showToast(currentProduct.id ? '产品已更新' : '产品已创建', 'success');
        setShowEditor(false);
        fetchProducts();
      } else {
        showToast('保存失败', 'error');
      }
    } catch (error) {
      showToast('保存失败', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // 发布/取消发布产品
  const handleTogglePublish = async (product) => {
    const newStatus = product.status === 'published' ? 'draft' : 'published';
    try {
      const response = await fetch(
        ADMIN_API_ENDPOINTS.PRODUCTS.BY_ID(product.id),
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem(ADMIN_TOKEN_KEY)}`,
          },
          body: JSON.stringify({ ...product, status: newStatus }),
        }
      );
      if (response.ok) {
        showToast(
          newStatus === 'published' ? '产品已发布' : '产品已下架',
          'success'
        );
        fetchProducts();
      }
    } catch (error) {
      showToast('操作失败', 'error');
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white mb-2">产品管理</h1>
          <p className="text-gray-400 text-sm">
            管理网站产品，添加新产品后将自动同步到前端展示
          </p>
        </div>
        <button
          onClick={handleCreateProduct}
          className="flex items-center gap-2 px-4 py-2 bg-[#f5a623] hover:bg-[#d4881c] text-white rounded-lg transition-colors"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            fill="currentColor"
            viewBox="0 0 256 256"
          >
            <path d="M224,128a8,8,0,0,1-8,8H136v80a8,8,0,0,1-16,0V136H40a8,8,0,0,1,0-16h80V40a8,8,0,0,1,16,0v80h80A8,8,0,0,1,224,128Z"></path>
          </svg>
          创建新产品
        </button>
      </div>

      {/* 产品列表 */}
      <div className="bg-[#1a1d17] border border-[#2a2d27] rounded-lg overflow-hidden">
        {isLoading && !showEditor ? (
          <div className="p-8 text-center text-gray-400">
            <div className="inline-block">
              <div className="w-8 h-8 border-2 border-gray-400 border-t-white rounded-full animate-spin"></div>
            </div>
            <p className="mt-2">加载中...</p>
          </div>
        ) : products.length === 0 ? (
          <div className="p-8 text-center text-gray-400">
            暂无产品,点击上方按钮创建第一个产品
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#2a2d27]">
                  <th className="text-left p-4 text-gray-400 font-medium">
                    产品名称
                  </th>
                  <th className="text-left p-4 text-gray-400 font-medium">
                    英文名称
                  </th>
                  <th className="text-left p-4 text-gray-400 font-medium">
                    产品ID
                  </th>
                  <th className="text-left p-4 text-gray-400 font-medium">
                    状态
                  </th>
                  <th className="text-left p-4 text-gray-400 font-medium">
                    操作
                  </th>
                </tr>
              </thead>
              <tbody>
                {products.map((product) => (
                  <tr
                    key={product.id}
                    className="border-b border-[#2a2d27] hover:bg-[#262a22] transition-colors"
                  >
                    <td className="p-4 text-white font-medium">
                      {product.name?.zh || '未命名产品'}
                    </td>
                    <td className="p-4 text-gray-400">
                      {product.name?.en || 'Unnamed Product'}
                    </td>
                    <td className="p-4 text-gray-400 font-mono text-sm">
                      {product.product_id}
                    </td>
                    <td className="p-4">
                      <span
                        className={`px-2 py-1 rounded text-xs border ${product.status === 'published'
                          ? 'bg-green-500/20 text-green-400 border-green-500/50'
                          : 'bg-gray-500/20 text-gray-400 border-gray-500/50'
                          }`}
                      >
                        {product.status === 'published' ? '已发布' : '草稿'}
                      </span>
                    </td>
                    <td className="p-4">
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEditProduct(product)}
                          className="px-3 py-1 bg-[#4a5f3a] hover:bg-[#5a7f4a] text-white rounded text-sm transition-colors"
                        >
                          编辑
                        </button>
                        <button
                          onClick={() => handleTogglePublish(product)}
                          className={`px-3 py-1 rounded text-sm transition-colors ${product.status === 'published'
                            ? 'bg-orange-500/20 hover:bg-orange-500/30 text-orange-400'
                            : 'bg-green-500/20 hover:bg-green-500/30 text-green-400'
                            }`}
                        >
                          {product.status === 'published' ? '下架' : '发布'}
                        </button>
                        <button
                          onClick={() => handleDeleteProduct(product.id)}
                          className="px-3 py-1 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded text-sm transition-colors"
                        >
                          删除
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* 产品编辑器 */}
      {showEditor && currentProduct && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-[#12140f] border-2 border-[#2a2d27] rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-[#12140f] border-b border-[#2a2d27] p-6 flex justify-between items-center">
              <h2 className="text-xl font-bold text-white">
                {currentProduct.id ? '编辑产品' : '创建新产品'}
              </h2>
              <button
                onClick={() => setShowEditor(false)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  fill="currentColor"
                  viewBox="0 0 256 256"
                >
                  <path d="M205.66,194.34a8,8,0,0,1-11.32,11.32L128,139.31,61.66,205.66a8,8,0,0,1-11.32-11.32L116.69,128,50.34,61.66A8,8,0,0,1,61.66,50.34L128,116.69l66.34-66.35a8,8,0,0,1,11.32,11.32L139.31,128Z"></path>
                </svg>
              </button>
            </div>

            <form onSubmit={handleSaveProduct} className="p-6 space-y-6">
              {/* 产品ID */}
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">
                  产品ID {!currentProduct.id && <span className="text-red-400">*</span>}
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={currentProduct.product_id || ''}
                    className="flex-1 px-4 py-2 bg-[#1a1d17] border border-[#2a2d27] rounded-lg text-white focus:outline-none cursor-not-allowed"
                    placeholder={currentProduct.id ? "系统生成的ID" : "点击按钮自动生成ID"}
                    disabled
                    readOnly
                  />
                  {!currentProduct.id && (
                    <button
                      type="button"
                      onClick={() => {
                        // 生成临时ID: product-时间戳-随机字符
                        const timestamp = Date.now().toString(36);
                        const randomStr = Math.random().toString(36).substring(2, 8);
                        const generatedId = `product-${timestamp}-${randomStr}`;
                        setCurrentProduct({
                          ...currentProduct,
                          product_id: generatedId,
                        });
                      }}
                      className="px-4 py-2 bg-[#f5a623] hover:bg-[#d4900e] text-black font-medium rounded-lg transition-colors whitespace-nowrap"
                    >
                      自动生成ID
                    </button>
                  )}
                </div>
                {!currentProduct.id && !currentProduct.product_id && (
                  <p className="text-xs text-gray-500 mt-1">
                    请先生成产品ID,然后才能填写其他信息
                  </p>
                )}
                {currentProduct.id && (
                  <p className="text-xs text-gray-500 mt-1">
                    编辑时不能修改产品ID
                  </p>
                )}
              </div>

              {/* 表单内容 */}
              <fieldset
                disabled={!currentProduct.product_id}
                className="space-y-6 border-none p-0 m-0"
              >
                {/* 产品名称 */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">
                      中文名称
                    </label>
                    <input
                      type="text"
                      value={currentProduct.name?.zh || ''}
                      onChange={(e) =>
                        setCurrentProduct({
                          ...currentProduct,
                          name: { ...currentProduct.name, zh: e.target.value },
                        })
                      }
                      className="w-full px-4 py-2 bg-[#1a1d17] border border-[#2a2d27] rounded-lg text-white focus:outline-none focus:border-[#f5a623]"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">
                      English Name
                    </label>
                    <input
                      type="text"
                      value={currentProduct.name?.en || ''}
                      onChange={(e) =>
                        setCurrentProduct({
                          ...currentProduct,
                          name: { ...currentProduct.name, en: e.target.value },
                        })
                      }
                      className="w-full px-4 py-2 bg-[#1a1d17] border border-[#2a2d27] rounded-lg text-white focus:outline-none focus:border-[#f5a623]"
                      required
                    />
                  </div>
                </div>

                {/* 产品描述 */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">
                      中文描述
                    </label>
                    <textarea
                      value={currentProduct.description?.zh || ''}
                      onChange={(e) =>
                        setCurrentProduct({
                          ...currentProduct,
                          description: {
                            ...currentProduct.description,
                            zh: e.target.value,
                          },
                        })
                      }
                      rows={4}
                      className="w-full px-4 py-2 bg-[#1a1d17] border border-[#2a2d27] rounded-lg text-white focus:outline-none focus:border-[#f5a623] resize-none"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">
                      English Description
                    </label>
                    <textarea
                      value={currentProduct.description?.en || ''}
                      onChange={(e) =>
                        setCurrentProduct({
                          ...currentProduct,
                          description: {
                            ...currentProduct.description,
                            en: e.target.value,
                          },
                        })
                      }
                      rows={4}
                      className="w-full px-4 py-2 bg-[#1a1d17] border border-[#2a2d27] rounded-lg text-white focus:outline-none focus:border-[#f5a623] resize-none"
                      required
                    />
                  </div>
                </div>

                {/* 主图URL */}
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    主图URL（中文描述下方显示的图片）
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={currentProduct.images?.[0] || ''}
                      onChange={(e) =>
                        setCurrentProduct({
                          ...currentProduct,
                          images: [
                            e.target.value,
                            ...(currentProduct.images?.slice(1) || []),
                          ],
                        })
                      }
                      placeholder="例如: /src/assets/products/tiger-i-mid/main.jpg"
                      className="flex-1 px-4 py-2 bg-[#1a1d17] border border-[#2a2d27] rounded-lg text-white focus:outline-none focus:border-[#f5a623]"
                    />
                    <label className={`px-4 py-2 ${uploadingImage === 0 ? 'bg-gray-500 cursor-not-allowed' : 'bg-[#4a5f3a] hover:bg-[#5a6f4a] cursor-pointer'} text-white rounded-lg transition-colors flex items-center gap-2 whitespace-nowrap`}>
                      {uploadingImage === 0 ? (
                        <>
                          <svg
                            className="animate-spin h-5 w-5"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          上传中...
                        </>
                      ) : (
                        <>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="20"
                            height="20"
                            fill="currentColor"
                            viewBox="0 0 256 256"
                          >
                            <path d="M216,40H40A16,16,0,0,0,24,56V200a16,16,0,0,0,16,16H216a16,16,0,0,0,16-16V56A16,16,0,0,0,216,40Zm0,16V158.75l-26.07-26.06a16,16,0,0,0-22.63,0l-20,20-44-44a16,16,0,0,0-22.62,0L40,149.37V56ZM40,172l52-52,80,80H40Zm176,28H194.63l-36-36,20-20L216,181.38V200ZM144,100a12,12,0,1,1,12,12A12,12,0,0,1,144,100Z"></path>
                          </svg>
                          上传
                        </>
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageUpload(e, 0)}
                        className="hidden"
                        disabled={uploadingImage === 0}
                      />
                    </label>
                  </div>
                  {/* 主图预览 */}
                  {currentProduct.images?.[0] && (
                    <div className="mt-3">
                      <label className="block text-sm font-medium text-gray-400 mb-2">
                        预览
                      </label>
                      <div className="relative w-full h-48 bg-[#1a1d17] border border-[#2a2d27] rounded-lg overflow-hidden">
                        <img
                          src={getImageUrl(currentProduct.images[0])}
                          alt="主图预览"
                          className="w-full h-full object-contain"
                          onError={(e) => {
                            e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect fill="%23333" width="200" height="200"/%3E%3Ctext fill="%23666" x="50%25" y="50%25" text-anchor="middle" dy=".3em"%3E图片加载失败%3C/text%3E%3C/svg%3E';
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* 主要参数 */}
                <div className="border-t border-[#2a2d27] pt-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold text-white">主要参数</h3>
                    <button
                      type="button"
                      onClick={() => {
                        const newSpecs = currentProduct.specifications || {};
                        // 生成唯一的key
                        const newKey = `param_${Date.now()}`;
                        setCurrentProduct({
                          ...currentProduct,
                          specifications: {
                            ...newSpecs,
                            [newKey]: { zh: '', en: '' },
                          },
                        });
                      }}
                      className="flex items-center gap-2 px-3 py-1.5 bg-[#f5a623] hover:bg-[#d4881c] text-white text-sm rounded transition-colors"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        fill="currentColor"
                        viewBox="0 0 256 256"
                      >
                        <path d="M224,128a8,8,0,0,1-8,8H136v80a8,8,0,0,1-16,0V136H40a8,8,0,0,1,0-16h80V40a8,8,0,0,1,16,0v80h80A8,8,0,0,1,224,128Z"></path>
                      </svg>
                      添加参数
                    </button>
                  </div>

                  <div className="space-y-4">
                    {currentProduct.specifications &&
                      Object.keys(currentProduct.specifications).length > 0 ? (
                      Object.entries(currentProduct.specifications).map(
                        ([key, value], index) => (
                          <div
                            key={key}
                            className="p-4 bg-[#262a22] border border-[#4b5445] rounded-lg"
                          >
                            <div className="flex items-start gap-4">
                              <div className="flex-1 space-y-3">
                                {/* 参数名称 */}
                                <div className="grid grid-cols-2 gap-4">
                                  <div>
                                    <label className="block text-sm font-medium text-gray-400 mb-2">
                                      参数名称（中文）
                                    </label>
                                    <input
                                      type="text"
                                      value={value.label?.zh || key}
                                      onChange={(e) => {
                                        const newSpecs = {
                                          ...currentProduct.specifications,
                                        };
                                        newSpecs[key] = {
                                          ...newSpecs[key],
                                          label: {
                                            ...newSpecs[key].label,
                                            zh: e.target.value,
                                          },
                                        };
                                        setCurrentProduct({
                                          ...currentProduct,
                                          specifications: newSpecs,
                                        });
                                      }}
                                      placeholder="例如: 比例、材质、尺寸"
                                      className="w-full px-3 py-2 bg-[#1a1d17] border border-[#2a2d27] rounded-lg text-white focus:outline-none focus:border-[#f5a623] text-sm"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-sm font-medium text-gray-400 mb-2">
                                      Parameter Name (English)
                                    </label>
                                    <input
                                      type="text"
                                      value={value.label?.en || key}
                                      onChange={(e) => {
                                        const newSpecs = {
                                          ...currentProduct.specifications,
                                        };
                                        newSpecs[key] = {
                                          ...newSpecs[key],
                                          label: {
                                            ...newSpecs[key].label,
                                            en: e.target.value,
                                          },
                                        };
                                        setCurrentProduct({
                                          ...currentProduct,
                                          specifications: newSpecs,
                                        });
                                      }}
                                      placeholder="e.g., Scale, Material, Dimensions"
                                      className="w-full px-3 py-2 bg-[#1a1d17] border border-[#2a2d27] rounded-lg text-white focus:outline-none focus:border-[#f5a623] text-sm"
                                    />
                                  </div>
                                </div>

                                {/* 参数值 */}
                                <div className="grid grid-cols-2 gap-4">
                                  <div>
                                    <label className="block text-sm font-medium text-gray-400 mb-2">
                                      参数值（中文）
                                    </label>
                                    <input
                                      type="text"
                                      value={value.zh || ''}
                                      onChange={(e) => {
                                        const newSpecs = {
                                          ...currentProduct.specifications,
                                        };
                                        newSpecs[key] = {
                                          ...newSpecs[key],
                                          zh: e.target.value,
                                        };
                                        setCurrentProduct({
                                          ...currentProduct,
                                          specifications: newSpecs,
                                        });
                                      }}
                                      placeholder="例如: 1:6"
                                      className="w-full px-3 py-2 bg-[#1a1d17] border border-[#2a2d27] rounded-lg text-white focus:outline-none focus:border-[#f5a623] text-sm"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-sm font-medium text-gray-400 mb-2">
                                      Value (English)
                                    </label>
                                    <input
                                      type="text"
                                      value={value.en || ''}
                                      onChange={(e) => {
                                        const newSpecs = {
                                          ...currentProduct.specifications,
                                        };
                                        newSpecs[key] = {
                                          ...newSpecs[key],
                                          en: e.target.value,
                                        };
                                        setCurrentProduct({
                                          ...currentProduct,
                                          specifications: newSpecs,
                                        });
                                      }}
                                      placeholder="e.g., 1:6"
                                      className="w-full px-3 py-2 bg-[#1a1d17] border border-[#2a2d27] rounded-lg text-white focus:outline-none focus:border-[#f5a623] text-sm"
                                    />
                                  </div>
                                </div>
                              </div>

                              {/* 删除按钮 */}
                              <button
                                type="button"
                                onClick={() => {
                                  const newSpecs = {
                                    ...currentProduct.specifications,
                                  };
                                  delete newSpecs[key];
                                  setCurrentProduct({
                                    ...currentProduct,
                                    specifications: newSpecs,
                                  });
                                }}
                                className="mt-8 px-3 py-2 bg-red-600/20 hover:bg-red-600/30 text-red-400 rounded-lg transition-colors"
                                title="删除此参数"
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="16"
                                  height="16"
                                  fill="currentColor"
                                  viewBox="0 0 256 256"
                                >
                                  <path d="M216,48H176V40a24,24,0,0,0-24-24H104A24,24,0,0,0,80,40v8H40a8,8,0,0,0,0,16h8V208a16,16,0,0,0,16,16H192a16,16,0,0,0,16-16V64h8a8,8,0,0,0,0-16ZM96,40a8,8,0,0,1,8-8h48a8,8,0,0,1,8,8v8H96Zm96,168H64V64H192ZM112,104v64a8,8,0,0,1-16,0V104a8,8,0,0,1,16,0Zm48,0v64a8,8,0,0,1-16,0V104a8,8,0,0,1,16,0Z"></path>
                                </svg>
                              </button>
                            </div>
                          </div>
                        )
                      )
                    ) : (
                      <div className="text-center py-8 text-gray-400 bg-[#262a22] border border-[#4b5445] rounded-lg">
                        <p>暂无参数，点击上方"添加参数"按钮添加</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* 细节图片 */}
                <div className="border-t border-[#2a2d27] pt-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold text-white">
                      细节图片（多张）
                    </h3>
                    <button
                      type="button"
                      onClick={() => {
                        const newImages = [...(currentProduct.images || [])];
                        newImages.push('');
                        setCurrentProduct({
                          ...currentProduct,
                          images: newImages,
                        });
                      }}
                      className="flex items-center gap-2 px-3 py-1.5 bg-[#f5a623] hover:bg-[#d4881c] text-white text-sm rounded transition-colors"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        fill="currentColor"
                        viewBox="0 0 256 256"
                      >
                        <path d="M224,128a8,8,0,0,1-8,8H136v80a8,8,0,0,1-16,0V136H40a8,8,0,0,1,0-16h80V40a8,8,0,0,1,16,0v80h80A8,8,0,0,1,224,128Z"></path>
                      </svg>
                      添加图片
                    </button>
                  </div>
                  <div className="space-y-3">
                    {(currentProduct.images || [])
                      .slice(1)
                      .map((image, index) => {
                        const actualIndex = index + 1;
                        return (
                          <div key={actualIndex} className="flex gap-2">
                            <div className="flex-1">
                              <label className="block text-sm font-medium text-gray-400 mb-2">
                                细节图 {actualIndex}
                              </label>
                              <div className="flex gap-2">
                                <input
                                  type="text"
                                  value={image || ''}
                                  onChange={(e) => {
                                    const newImages = [
                                      ...(currentProduct.images || []),
                                    ];
                                    newImages[actualIndex] = e.target.value;
                                    setCurrentProduct({
                                      ...currentProduct,
                                      images: newImages,
                                    });
                                  }}
                                  placeholder={`细节图片 ${actualIndex} URL`}
                                  className="flex-1 px-4 py-2 bg-[#1a1d17] border border-[#2a2d27] rounded-lg text-white focus:outline-none focus:border-[#f5a623]"
                                />
                                <label className={`px-4 py-2 ${uploadingImage === actualIndex ? 'bg-gray-500 cursor-not-allowed' : 'bg-[#4a5f3a] hover:bg-[#5a6f4a] cursor-pointer'} text-white rounded-lg transition-colors flex items-center gap-2 whitespace-nowrap`}>
                                  {uploadingImage === actualIndex ? (
                                    <>
                                      <svg
                                        className="animate-spin h-5 w-5"
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                      >
                                        <circle
                                          className="opacity-25"
                                          cx="12"
                                          cy="12"
                                          r="10"
                                          stroke="currentColor"
                                          strokeWidth="4"
                                        ></circle>
                                        <path
                                          className="opacity-75"
                                          fill="currentColor"
                                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                        ></path>
                                      </svg>
                                      上传中...
                                    </>
                                  ) : (
                                    <>
                                      <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="20"
                                        height="20"
                                        fill="currentColor"
                                        viewBox="0 0 256 256"
                                      >
                                        <path d="M216,40H40A16,16,0,0,0,24,56V200a16,16,0,0,0,16,16H216a16,16,0,0,0,16-16V56A16,16,0,0,0,216,40Zm0,16V158.75l-26.07-26.06a16,16,0,0,0-22.63,0l-20,20-44-44a16,16,0,0,0-22.62,0L40,149.37V56ZM40,172l52-52,80,80H40Zm176,28H194.63l-36-36,20-20L216,181.38V200ZM144,100a12,12,0,1,1,12,12A12,12,0,0,1,144,100Z"></path>
                                      </svg>
                                      上传
                                    </>
                                  )}
                                  <input
                                    type="file"
                                    accept="image/*"
                                    onChange={(e) =>
                                      handleImageUpload(e, actualIndex)
                                    }
                                    className="hidden"
                                    disabled={uploadingImage === actualIndex}
                                  />
                                </label>
                              </div>
                              {/* 细节图片预览 */}
                              {currentProduct.images?.[actualIndex] && (
                                <div className="mt-3">
                                  <label className="block text-sm font-medium text-gray-400 mb-2">
                                    预览
                                  </label>
                                  <div className="relative w-full h-32 bg-[#1a1d17] border border-[#2a2d27] rounded-lg overflow-hidden">
                                    <img
                                      src={getImageUrl(currentProduct.images[actualIndex])}
                                      alt={`细节图片 ${actualIndex} 预览`}
                                      className="w-full h-full object-contain"
                                      onError={(e) => {
                                        e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect fill="%23333" width="200" height="200"/%3E%3Ctext fill="%23666" x="50%25" y="50%25" text-anchor="middle" dy=".3em"%3E图片加载失败%3C/text%3E%3C/svg%3E';
                                      }}
                                    />
                                  </div>
                                </div>
                              )}
                            </div>
                            <button
                              type="button"
                              onClick={() => {
                                const newImages = [
                                  ...(currentProduct.images || []),
                                ];
                                newImages.splice(actualIndex, 1);
                                setCurrentProduct({
                                  ...currentProduct,
                                  images: newImages,
                                });
                              }}
                              className="mt-8 px-3 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded transition-colors"
                              title="删除此图片"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="20"
                                height="20"
                                fill="currentColor"
                                viewBox="0 0 256 256"
                              >
                                <path d="M216,48H176V40a24,24,0,0,0-24-24H104A24,24,0,0,0,80,40v8H40a8,8,0,0,0,0,16h8V208a16,16,0,0,0,16,16H192a16,16,0,0,0,16-16V64h8a8,8,0,0,0,0-16ZM96,40a8,8,0,0,1,8-8h48a8,8,0,0,1,8,8v8H96Zm96,168H64V64H192ZM112,104v64a8,8,0,0,1-16,0V104a8,8,0,0,1,16,0Zm48,0v64a8,8,0,0,1-16,0V104a8,8,0,0,1,16,0Z"></path>
                              </svg>
                            </button>
                          </div>
                        );
                      })}
                    {(!currentProduct.images ||
                      currentProduct.images.length <= 1) && (
                        <div className="text-center py-8 border-2 border-dashed border-[#2a2d27] rounded-lg">
                          <p className="text-gray-500 mb-3">暂无细节图片</p>
                          <p className="text-gray-600 text-sm">
                            点击上方"添加图片"按钮添加细节图片
                          </p>
                        </div>
                      )}
                  </div>
                </div>

                {/* 历史简介 */}
                <div className="border-t border-[#2a2d27] pt-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold text-white">历史简介</h3>
                    <button
                      type="button"
                      onClick={() => {
                        const newItems = [
                          ...(currentProduct.historyItems || []),
                          { text: { zh: '', en: '' }, media: { type: 'image', url: '' } },
                        ];
                        console.log('Adding history group, new items:', newItems);
                        setCurrentProduct({
                          ...currentProduct,
                          historyItems: newItems,
                        });
                      }}
                      className="flex items-center gap-2 px-4 py-2 bg-[#4a5f3a] hover:bg-[#5a6f4a] text-white rounded-lg transition-colors text-sm"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        fill="currentColor"
                        viewBox="0 0 256 256"
                      >
                        <path d="M224,128a8,8,0,0,1-8,8H136v80a8,8,0,0,1-16,0V136H40a8,8,0,0,1,0-16h80V40a8,8,0,0,1,16,0v80h80A8,8,0,0,1,224,128Z"></path>
                      </svg>
                      添加历史简介组
                    </button>
                  </div>

                  <div className="space-y-4">
                    {currentProduct.historyItems && currentProduct.historyItems.length > 0 ? (
                      currentProduct.historyItems.map((item, index) => (
                        <div
                          key={index}
                          className="p-4 bg-[#262a22] border border-[#4b5445] rounded-lg"
                        >
                          <div className="flex gap-4">
                            <div className="flex-1 space-y-4">
                              {/* 文字内容 */}
                              <div>
                                <label className="block text-sm font-medium text-gray-400 mb-2">
                                  文字内容（可选）
                                </label>
                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <label className="block text-xs text-gray-500 mb-1">中文</label>
                                    <textarea
                                      value={item.text?.zh || ''}
                                      onChange={(e) => {
                                        const newItems = [...(currentProduct.historyItems || [])];
                                        newItems[index] = {
                                          ...newItems[index],
                                          text: {
                                            ...newItems[index].text,
                                            zh: e.target.value,
                                          },
                                        };
                                        setCurrentProduct({
                                          ...currentProduct,
                                          historyItems: newItems,
                                        });
                                      }}
                                      rows={3}
                                      placeholder="输入中文历史简介..."
                                      className="w-full px-3 py-2 bg-[#1a1d17] border border-[#2a2d27] rounded-lg text-white text-sm focus:outline-none focus:border-[#f5a623] resize-none"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-xs text-gray-500 mb-1">English</label>
                                    <textarea
                                      value={item.text?.en || ''}
                                      onChange={(e) => {
                                        const newItems = [...(currentProduct.historyItems || [])];
                                        newItems[index] = {
                                          ...newItems[index],
                                          text: {
                                            ...newItems[index].text,
                                            en: e.target.value,
                                          },
                                        };
                                        setCurrentProduct({
                                          ...currentProduct,
                                          historyItems: newItems,
                                        });
                                      }}
                                      rows={3}
                                      placeholder="Enter English history..."
                                      className="w-full px-3 py-2 bg-[#1a1d17] border border-[#2a2d27] rounded-lg text-white text-sm focus:outline-none focus:border-[#f5a623] resize-none"
                                    />
                                  </div>
                                </div>
                              </div>

                              {/* 媒体内容 */}
                              <div>
                                <div className="flex items-center justify-between mb-2">
                                  <label className="block text-sm font-medium text-gray-400">
                                    媒体内容（可选）
                                  </label>
                                  {item.media && (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const newItems = [...(currentProduct.historyItems || [])];
                                        newItems[index] = {
                                          ...newItems[index],
                                          media: null,
                                        };
                                        setCurrentProduct({
                                          ...currentProduct,
                                          historyItems: newItems,
                                        });
                                      }}
                                      className="text-xs text-red-400 hover:text-red-300"
                                    >
                                      移除媒体
                                    </button>
                                  )}
                                </div>

                                {!item.media ? (
                                  <div className="flex gap-2">
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const newItems = [...(currentProduct.historyItems || [])];
                                        newItems[index] = {
                                          ...newItems[index],
                                          media: { type: 'image', url: '' },
                                        };
                                        setCurrentProduct({
                                          ...currentProduct,
                                          historyItems: newItems,
                                        });
                                      }}
                                      className="flex-1 px-3 py-2 bg-[#1a1d17] border border-[#2a2d27] hover:border-[#4a5f3a] rounded-lg text-gray-400 text-sm transition-colors"
                                    >
                                      + 添加图片
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const newItems = [...(currentProduct.historyItems || [])];
                                        newItems[index] = {
                                          ...newItems[index],
                                          media: { type: 'video', url: '' },
                                        };
                                        setCurrentProduct({
                                          ...currentProduct,
                                          historyItems: newItems,
                                        });
                                      }}
                                      className="flex-1 px-3 py-2 bg-[#1a1d17] border border-[#2a2d27] hover:border-[#4a5f3a] rounded-lg text-gray-400 text-sm transition-colors"
                                    >
                                      + 添加视频
                                    </button>
                                  </div>
                                ) : (
                                  <div className="space-y-2">
                                    {/* 媒体类型显示 */}
                                    <div className="text-xs text-gray-500">
                                      类型: {item.media.type === 'image' ? '图片' : '视频'}
                                    </div>

                                    {/* 图片上传 */}
                                    {item.media.type === 'image' && (
                                      <div className="space-y-2">
                                        <div className="flex gap-2">
                                          <input
                                            type="text"
                                            value={item.media.url || ''}
                                            onChange={(e) => {
                                              const newItems = [...(currentProduct.historyItems || [])];
                                              newItems[index] = {
                                                ...newItems[index],
                                                media: {
                                                  ...newItems[index].media,
                                                  url: e.target.value,
                                                },
                                              };
                                              setCurrentProduct({
                                                ...currentProduct,
                                                historyItems: newItems,
                                              });
                                            }}
                                            placeholder="图片URL或上传"
                                            className="flex-1 px-3 py-2 bg-[#1a1d17] border border-[#2a2d27] rounded-lg text-white text-sm focus:outline-none focus:border-[#f5a623]"
                                          />
                                          <label className={`px-4 py-2 ${uploadingHistoryImage === index ? 'bg-gray-500 cursor-not-allowed' : 'bg-[#4a5f3a] hover:bg-[#5a6f4a] cursor-pointer'} text-white rounded-lg transition-colors flex items-center gap-2 whitespace-nowrap text-sm`}>
                                            {uploadingHistoryImage === index ? (
                                              <>
                                                <svg
                                                  className="animate-spin h-4 w-4"
                                                  xmlns="http://www.w3.org/2000/svg"
                                                  fill="none"
                                                  viewBox="0 0 24 24"
                                                >
                                                  <circle
                                                    className="opacity-25"
                                                    cx="12"
                                                    cy="12"
                                                    r="10"
                                                    stroke="currentColor"
                                                    strokeWidth="4"
                                                  ></circle>
                                                  <path
                                                    className="opacity-75"
                                                    fill="currentColor"
                                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                                  ></path>
                                                </svg>
                                                上传中...
                                              </>
                                            ) : (
                                              <>
                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 256 256">
                                                  <path d="M216,40H40A16,16,0,0,0,24,56V200a16,16,0,0,0,16,16H216a16,16,0,0,0,16-16V56A16,16,0,0,0,216,40Zm0,16V158.75l-26.07-26.06a16,16,0,0,0-22.63,0l-20,20-44-44a16,16,0,0,0-22.62,0L40,149.37V56ZM40,172l52-52,80,80H40Zm176,28H194.63l-36-36,20-20L216,181.38V200ZM144,100a12,12,0,1,1,12,12A12,12,0,0,1,144,100Z"></path>
                                                </svg>
                                                上传
                                              </>
                                            )}
                                            <input
                                              type="file"
                                              accept="image/*"
                                              onChange={async (e) => {
                                                const file = e.target.files[0];
                                                if (!file) return;

                                                if (!file.type.startsWith('image/')) {
                                                  showToast('请选择图片文件', 'error');
                                                  e.target.value = '';
                                                  return;
                                                }

                                                if (file.size > 30 * 1024 * 1024) {
                                                  showToast('图片大小不能超过30MB', 'error');
                                                  e.target.value = '';
                                                  return;
                                                }

                                                const formData = new FormData();
                                                formData.append('image', file);
                                                formData.append('productId', currentProduct.product_id);

                                                try {
                                                  setUploadingHistoryImage(index);
                                                  const response = await fetch(ADMIN_API_ENDPOINTS.PRODUCTS.UPLOAD_IMAGE, {
                                                    method: 'POST',
                                                    headers: {
                                                      Authorization: `Bearer ${localStorage.getItem(ADMIN_TOKEN_KEY)}`,
                                                    },
                                                    body: formData,
                                                  });

                                                  const data = await response.json();

                                                  if (data.success) {
                                                    const newItems = [...(currentProduct.historyItems || [])];
                                                    newItems[index] = {
                                                      ...newItems[index],
                                                      media: {
                                                        ...newItems[index].media,
                                                        url: data.imageUrl,
                                                      },
                                                    };
                                                    setCurrentProduct((prev) => {
                                                      const newItems = [...(prev.historyItems || [])];
                                                      newItems[index] = {
                                                        ...newItems[index],
                                                        media: {
                                                          ...newItems[index].media,
                                                          url: data.imageUrl,
                                                        },
                                                      };
                                                      return {
                                                        ...prev,
                                                        historyItems: newItems,
                                                      };
                                                    });
                                                    showToast('图片上传成功', 'success');
                                                  } else {
                                                    showToast(data.message || '上传失败', 'error');
                                                  }
                                                } catch (error) {
                                                  console.error('Upload error:', error);
                                                  showToast('上传失败', 'error');
                                                } finally {
                                                  setUploadingHistoryImage(null);
                                                  e.target.value = '';
                                                }
                                              }}
                                              className="hidden"
                                            />
                                          </label>
                                        </div>
                                        {/* 图片预览 */}
                                        {item.media.url && (
                                          <div className="relative w-full h-32 bg-[#1a1d17] border border-[#2a2d27] rounded-lg overflow-hidden">
                                            <img
                                              src={getImageUrl(item.media.url)}
                                              alt="预览"
                                              className="w-full h-full object-contain"
                                              onError={(e) => {
                                                e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect fill="%23333" width="200" height="200"/%3E%3Ctext fill="%23666" x="50%25" y="50%25" text-anchor="middle" dy=".3em"%3E图片加载失败%3C/text%3E%3C/svg%3E';
                                              }}
                                            />
                                          </div>
                                        )}
                                      </div>
                                    )}

                                    {/* 视频上传 */}
                                    {item.media.type === 'video' && (
                                      <div className="space-y-2">
                                        <div className="flex gap-2">
                                          <input
                                            type="text"
                                            value={item.media.url || ''}
                                            onChange={(e) => {
                                              const newItems = [...(currentProduct.historyItems || [])];
                                              newItems[index] = {
                                                ...newItems[index],
                                                media: {
                                                  ...newItems[index].media,
                                                  url: e.target.value,
                                                },
                                              };
                                              setCurrentProduct((prev) => {
                                                const newItems = [...(prev.historyItems || [])];
                                                newItems[index] = {
                                                  ...newItems[index],
                                                  media: {
                                                    ...newItems[index].media,
                                                    url: e.target.value,
                                                  },
                                                };
                                                return {
                                                  ...prev,
                                                  historyItems: newItems,
                                                };
                                              });
                                            }}
                                            placeholder="视频URL"
                                            className="flex-1 px-3 py-2 bg-[#1a1d17] border border-[#2a2d27] rounded-lg text-white text-sm focus:outline-none focus:border-[#f5a623]"
                                          />
                                          <label className="px-4 py-2 bg-[#4a5f3a] hover:bg-[#5a6f4a] text-white rounded-lg cursor-pointer transition-colors flex items-center gap-2 whitespace-nowrap text-sm">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 256 256">
                                              <path d="M213.66,82.34l-56-56A8,8,0,0,0,152,24H56A16,16,0,0,0,40,40V216a16,16,0,0,0,16,16H200a16,16,0,0,0,16-16V88A8,8,0,0,0,213.66,82.34ZM160,51.31,188.69,80H160ZM200,216H56V40h88V88a8,8,0,0,0,8,8h48V216Zm-42.34-61.66a8,8,0,0,1,0,11.32l-24,24a8,8,0,0,1-11.32,0l-24-24a8,8,0,0,1,11.32-11.32L120,164.69V120a8,8,0,0,1,16,0v44.69l10.34-10.35A8,8,0,0,1,157.66,154.34Z"></path>
                                            </svg>
                                            上传视频
                                            <input
                                              type="file"
                                              accept="video/*"
                                              onChange={async (e) => {
                                                const file = e.target.files[0];
                                                if (!file) return;

                                                if (file.size > 100 * 1024 * 1024) {
                                                  showToast('视频文件大小不能超过100MB', 'error');
                                                  e.target.value = '';
                                                  return;
                                                }

                                                if (!file.type.startsWith('video/')) {
                                                  showToast('请选择视频文件', 'error');
                                                  e.target.value = '';
                                                  return;
                                                }

                                                const formData = new FormData();
                                                formData.append('video', file);
                                                formData.append('productId', currentProduct.product_id);

                                                try {
                                                  setIsLoading(true);
                                                  const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';
                                                  const response = await fetch(
                                                    `${API_BASE_URL.replace('/api', '')}/api/admin/products/upload-video`,
                                                    {
                                                      method: 'POST',
                                                      headers: {
                                                        Authorization: `Bearer ${localStorage.getItem(ADMIN_TOKEN_KEY)}`,
                                                      },
                                                      body: formData,
                                                    }
                                                  );

                                                  const data = await response.json();

                                                  if (data.success) {
                                                    const newItems = [...(currentProduct.historyItems || [])];
                                                    newItems[index] = {
                                                      ...newItems[index],
                                                      media: {
                                                        ...newItems[index].media,
                                                        url: data.videoUrl,
                                                      },
                                                    };
                                                    setCurrentProduct((prev) => {
                                                      const newItems = [...(prev.historyItems || [])];
                                                      newItems[index] = {
                                                        ...newItems[index],
                                                        media: {
                                                          ...newItems[index].media,
                                                          url: data.videoUrl,
                                                        },
                                                      };
                                                      return {
                                                        ...prev,
                                                        historyItems: newItems,
                                                      };
                                                    });
                                                    showToast('视频上传成功', 'success');
                                                  } else {
                                                    showToast(data.message || '上传失败', 'error');
                                                  }
                                                } catch (error) {
                                                  console.error('Upload error:', error);
                                                  showToast('上传失败', 'error');
                                                } finally {
                                                  setIsLoading(false);
                                                  e.target.value = '';
                                                }
                                              }}
                                              className="hidden"
                                            />
                                          </label>
                                        </div>
                                        <p className="text-xs text-gray-500">
                                          支持的格式: MP4, WebM, MOV 等，最大100MB
                                        </p>
                                        {/* 视频预览 */}
                                        {item.media.url && (
                                          <div className="relative w-full bg-[#1a1d17] border border-[#2a2d27] rounded-lg overflow-hidden">
                                            <video
                                              src={getImageUrl(item.media.url)}
                                              controls
                                              className="w-full max-h-48"
                                              onError={(e) => {
                                                console.error('Video load error:', item.media.url);
                                              }}
                                            >
                                              您的浏览器不支持视频播放
                                            </video>
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* 删除按钮 */}
                            <button
                              type="button"
                              onClick={() => {
                                const newItems = currentProduct.historyItems.filter(
                                  (_, i) => i !== index
                                );
                                setCurrentProduct({
                                  ...currentProduct,
                                  historyItems: newItems,
                                });
                              }}
                              className="px-3 py-2 bg-red-600/20 hover:bg-red-600/30 text-red-400 rounded-lg transition-colors h-fit"
                              title="删除此组"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="16"
                                height="16"
                                fill="currentColor"
                                viewBox="0 0 256 256"
                              >
                                <path d="M216,48H176V40a24,24,0,0,0-24-24H104A24,24,0,0,0,80,40v8H40a8,8,0,0,0,0,16h8V208a16,16,0,0,0,16,16H192a16,16,0,0,0,16-16V64h8a8,8,0,0,0,0-16ZM96,40a8,8,0,0,1,8-8h48a8,8,0,0,1,8,8v8H96Zm96,168H64V64H192ZM112,104v64a8,8,0,0,1-16,0V104a8,8,0,0,1,16,0Zm48,0v64a8,8,0,0,1-16,0V104a8,8,0,0,1,16,0Z"></path>
                              </svg>
                            </button>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-6 text-gray-400 bg-[#262a22] border border-[#4b5445] rounded-lg">
                        <p className="text-sm">暂无历史简介内容</p>
                        <p className="text-xs mt-1">点击上方"添加历史简介组"按钮开始添加</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* 参考链接 */}
                <div className="mt-6">
                  <div className="flex items-center justify-between mb-4">
                    <label className="block text-sm font-medium text-gray-400">
                      参考链接（可选）
                    </label>
                    <button
                      type="button"
                      onClick={() => {
                        const newLinks = currentProduct.referenceLinks || [];
                        newLinks.push({ title: { zh: '', en: '' }, url: '' });
                        setCurrentProduct({
                          ...currentProduct,
                          referenceLinks: newLinks,
                        });
                      }}
                      className="flex items-center gap-2 px-3 py-1.5 bg-[#4a5f3a] hover:bg-[#5a6f4a] text-white text-sm rounded transition-colors"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        fill="currentColor"
                        viewBox="0 0 256 256"
                      >
                        <path d="M224,128a8,8,0,0,1-8,8H136v80a8,8,0,0,1-16,0V136H40a8,8,0,0,1,0-16h80V40a8,8,0,0,1,16,0v80h80A8,8,0,0,1,224,128Z"></path>
                      </svg>
                      添加链接
                    </button>
                  </div>

                  <div className="space-y-3">
                    {currentProduct.referenceLinks &&
                      currentProduct.referenceLinks.length > 0 ? (
                      currentProduct.referenceLinks.map((link, index) => (
                        <div
                          key={index}
                          className="p-4 bg-[#262a22] border border-[#4b5445] rounded-lg"
                        >
                          <div className="flex gap-4">
                            <div className="flex-1 space-y-3">
                              {/* 链接标题 */}
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <label className="block text-sm font-medium text-gray-400 mb-2">
                                    链接标题（中文）
                                  </label>
                                  <input
                                    type="text"
                                    value={link.title?.zh || ''}
                                    onChange={(e) => {
                                      const newLinks = [
                                        ...(currentProduct.referenceLinks || []),
                                      ];
                                      newLinks[index] = {
                                        ...newLinks[index],
                                        title: {
                                          ...newLinks[index].title,
                                          zh: e.target.value,
                                        },
                                      };
                                      setCurrentProduct({
                                        ...currentProduct,
                                        referenceLinks: newLinks,
                                      });
                                    }}
                                    placeholder="例如: 维基百科 - 虎式坦克"
                                    className="w-full px-3 py-2 bg-[#1a1d17] border border-[#2a2d27] rounded-lg text-white focus:outline-none focus:border-[#f5a623] text-sm"
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-400 mb-2">
                                    Link Title (English)
                                  </label>
                                  <input
                                    type="text"
                                    value={link.title?.en || ''}
                                    onChange={(e) => {
                                      const newLinks = [
                                        ...(currentProduct.referenceLinks || []),
                                      ];
                                      newLinks[index] = {
                                        ...newLinks[index],
                                        title: {
                                          ...newLinks[index].title,
                                          en: e.target.value,
                                        },
                                      };
                                      setCurrentProduct({
                                        ...currentProduct,
                                        referenceLinks: newLinks,
                                      });
                                    }}
                                    placeholder="e.g., Wikipedia - Tiger Tank"
                                    className="w-full px-3 py-2 bg-[#1a1d17] border border-[#2a2d27] rounded-lg text-white focus:outline-none focus:border-[#f5a623] text-sm"
                                  />
                                </div>
                              </div>

                              {/* 链接URL */}
                              <div>
                                <label className="block text-sm font-medium text-gray-400 mb-2">
                                  链接URL
                                </label>
                                <input
                                  type="url"
                                  value={link.url || ''}
                                  onChange={(e) => {
                                    const newLinks = [
                                      ...(currentProduct.referenceLinks || []),
                                    ];
                                    newLinks[index] = {
                                      ...newLinks[index],
                                      url: e.target.value,
                                    };
                                    setCurrentProduct({
                                      ...currentProduct,
                                      referenceLinks: newLinks,
                                    });
                                  }}
                                  placeholder="https://example.com/article"
                                  className="w-full px-3 py-2 bg-[#1a1d17] border border-[#2a2d27] rounded-lg text-white focus:outline-none focus:border-[#f5a623] text-sm"
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                  提示: 输入完整的网址,包括 https://
                                </p>
                              </div>
                            </div>

                            {/* 删除按钮 */}
                            <button
                              type="button"
                              onClick={() => {
                                const newLinks = currentProduct.referenceLinks.filter(
                                  (_, i) => i !== index
                                );
                                setCurrentProduct({
                                  ...currentProduct,
                                  referenceLinks: newLinks,
                                });
                              }}
                              className="px-3 py-2 bg-red-600/20 hover:bg-red-600/30 text-red-400 rounded-lg transition-colors h-fit"
                              title="删除此链接"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="16"
                                height="16"
                                fill="currentColor"
                                viewBox="0 0 256 256"
                              >
                                <path d="M216,48H176V40a24,24,0,0,0-24-24H104A24,24,0,0,0,80,40v8H40a8,8,0,0,0,0,16h8V208a16,16,0,0,0,16,16H192a16,16,0,0,0,16-16V64h8a8,8,0,0,0,0-16ZM96,40a8,8,0,0,1,8-8h48a8,8,0,0,1,8,8v8H96Zm96,168H64V64H192ZM112,104v64a8,8,0,0,1-16,0V104a8,8,0,0,1,16,0Zm48,0v64a8,8,0,0,1-16,0V104a8,8,0,0,1,16,0Z"></path>
                              </svg>
                            </button>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-6 text-gray-400 bg-[#262a22] border border-[#4b5445] rounded-lg">
                        <p className="text-sm">
                          暂无参考链接，点击上方"添加链接"按钮添加外部参考资料
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* 提示信息 */}
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                  <p className="text-blue-400 text-sm">
                    💡
                    提示：保存后，产品将按照现有模板自动生成详情页面，并添加到网站产品菜单中
                  </p>
                </div>

              </fieldset>

              {/* 操作按钮 */}
              <div className="flex gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => setShowEditor(false)}
                  className="flex-1 px-6 py-3 bg-[#262a22] hover:bg-[#353b30] text-white rounded-lg transition-colors"
                >
                  取消
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="flex-1 px-6 py-3 bg-[#f5a623] hover:bg-[#d4881c] disabled:bg-[#f5a623]/50 text-white rounded-lg transition-colors"
                >
                  {isLoading ? '保存中...' : '保存产品'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Toast通知 */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      {/* 确认对话框 */}
      {confirmDialog && <ConfirmDialog {...confirmDialog} />}
    </div>
  );
};

export default ProductManagement;
